import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ChevronLeft, MoreVertical, Plus } from 'lucide-react';

import { useAppSelector } from '../../hooks.ts';
import { getFolderSelector } from '../../store/selectors/projects.selector.ts';
import { history } from '../../store/store.ts';
import { green01 } from '../../theme.ts';
import { Folder } from '../../types/folder';
import { Project } from '../../types/project.ts';
import { LoadingSpinner } from '../loading/LoadingSpinner';
import EmptyProjects from './../../assets/EmptyProjects.svg?react';
import FolderIconList from './../../assets/FolderIcon.svg?react';
import FolderIconNew from './../../assets/FolderIconNew.svg?react';
import { DashboardSearch } from './DashboardSearch';

interface ProjectGridProps {
  folders: Folder[];
  viewMode?: 'grid' | 'list';
  onProjectSelect: (projectId: number | null) => void;
  projects: Project[];
  exampleProjects: Project[];
  isLoading: boolean;
  onOpenContextMenu: (
    e: React.MouseEvent,
    item: Project | Folder,
    itemType: 'project' | 'folder' | 'exampleProject',
  ) => void;
  onFolderSelect: (folderId: number | undefined) => void;
  gridSize?: 'small' | 'medium' | 'large';
  hasNext?: boolean;
  isLoadingMore?: boolean;
  onLoadMore?: () => void;
  onTryExample?: (id: number) => void;
  onOpenRenameModal?: (item: Project | Folder) => void;
  onOpenMoveModal?: (item: Project | Folder) => void;
  onOpenDeleteModal?: (item: Project | Folder) => void;
  isAdmin?: boolean;
  onSetProjectTemplate?: (id: number) => void;
  onCreateProject?: () => void;
  /** The search shown next to the section title. */
  searchValue?: string;
  onSearchChange?: (value: string) => void;
}

const ProjectGrid: React.FC<ProjectGridProps> = ({
  folders,
  viewMode = 'grid',
  onProjectSelect,
  projects,
  exampleProjects = [],
  isLoading,
  onOpenContextMenu,
  onFolderSelect,
  gridSize = 'medium',
  hasNext = false,
  isLoadingMore = false,
  onLoadMore,
  onTryExample,
  onOpenRenameModal,
  onOpenMoveModal,
  onOpenDeleteModal,
  onCreateProject,
  searchValue = '',
  onSearchChange,
  isAdmin = false,
  onSetProjectTemplate,
}) => {
  const { t } = useTranslation();
  const [selectedProjectId, setSelectedProjectId] = React.useState<number | null>(null);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const folder = useAppSelector(getFolderSelector);
  const scrollRootRef = useRef<HTMLDivElement | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  const generateBreadcrumbs = () => {
    // Start with My Projects as the root
    const breadcrumbs: [{ name: string; id?: number; isLast?: boolean }] = [{ name: t('common.back'), id: undefined }];

    if (!folder) return breadcrumbs;

    // For root folder
    if (!folder.id && !folder.name) {
      return breadcrumbs;
    }

    // Add parent folders in order
    if (folder.parents && folder.parents.length > 0) {
      folder.parents.forEach((parent) => {
        if (parent.id && parent.name) {
          breadcrumbs.push({
            name: parent.name,
            id: parent.id,
          });
        }
      });
    }

    // Add current folder
    if (folder.id && folder.name) {
      breadcrumbs.push({
        name: folder.name,
        id: folder.id,
        isLast: true,
      });
    }

    return breadcrumbs;
  };

  const breadcrumbs = generateBreadcrumbs();

  const handleBreadcrumbClick = (folderId?: number) => {
    if (!folderId) {
      history.push('/');
    } else {
      history.push(`/?folderId=${folderId}`);
    }
  };

  const formatUpdatedDate = (date?: Date | string | null, fallback = 'N/A'): string => {
    if (!date) return fallback;
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return dateObj.toLocaleDateString();
  };

  const getGridColumns = () => {
    if (windowWidth < 768) {
      return 'repeat(auto-fit, minmax(140px, 1fr))';
    }
    switch (gridSize) {
      case 'small':
        return 'repeat(auto-fit, 160px)';
      case 'medium':
        return 'repeat(auto-fit, 200px)';
      case 'large':
        return 'repeat(auto-fit, 250px)';
      default:
        return 'repeat(auto-fit, 200px)';
    }
  };

  const getCardSize = () => {
    if (windowWidth < 768) {
      return { width: '100%', height: '140px' };
    }
    switch (gridSize) {
      case 'small':
        return { width: '160px', height: '120px' };
      case 'medium':
        return { width: '200px', height: '150px' };
      case 'large':
        return { width: '250px', height: '187px' };
      default:
        return { width: '200px', height: '150px' };
    }
  };

  useEffect(() => {
    const preventNativeMenu = (e: MouseEvent) => e.preventDefault();
    window.addEventListener('contextmenu', preventNativeMenu);
    return () => window.removeEventListener('contextmenu', preventNativeMenu);
  }, []);

  // Infinite scroll observer
  useEffect(() => {
    if (!hasNext || !onLoadMore) return;
    const rootEl = scrollRootRef.current;
    const sentinelEl = sentinelRef.current;
    if (!rootEl || !sentinelEl) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting && !isLoadingMore) {
          onLoadMore();
        }
      },
      { root: rootEl, rootMargin: '0px', threshold: 0.1 },
    );
    observer.observe(sentinelEl);
    return () => observer.disconnect();
  }, [hasNext, onLoadMore, isLoadingMore]);

  // TODO: refactored when PR merged - https://github.com/Taman-Skira/taman-backend/pull/165/files
  // const allProjects = [
  //   {
  //     id: 1,
  //     name: "Zadar coast",
  //     lastEdit: "15 minutes ago",
  //     members: ["IM", "KO", "DS"],
  //     status: "In progress",
  //     optimization: null,
  //     previewImageUrl: "src/assets/model_zadar.jpg",
  //     hasStatusIcon: true,
  //     inProgress: true
  //   },
  //   {
  //     id: 2,
  //     name: "The Walk at JBR by Dubai",
  //     lastEdit: "1 day ago",
  //     members: ["IM"],
  //     status: "Successful",
  //     optimization: 92,
  //     previewImageUrl: "src/assets/model_dubai.jpg",
  //     hasStatusIcon: true
  //   }
  // ];
  // const exampleProjects = allProjects.slice(0, 2);

  const handleCreateNewProject = () => {
    if (onCreateProject) {
      onCreateProject();
    } else {
      history.push('/new-project');
    }
  };

  // Helper to check if there are any projects or folder children to display
  const hasProjectsOrFolderContent = () => {
    return projects.length > 0 || folders.some((folder) => folder.children && folder.children.length > 0);
  };

  const handleContextMenu = (
    e: React.MouseEvent,
    item: Project | Folder,
    itemType: 'project' | 'folder' | 'exampleProject',
  ) => {
    e.preventDefault();
    onOpenContextMenu(e, item, itemType);
  };

  const handleProjectClick = (e: React.MouseEvent<HTMLDivElement>, project: Project) => {
    switch (e.detail) {
      case 1:
        if (selectedProjectId === project.id) {
          setSelectedProjectId(null);
          onProjectSelect?.(null);
        } else {
          setSelectedProjectId(project.id);
          onProjectSelect?.(project.id);
        }
        break;
      case 2:
        window.location.href = `/workspace?projectId=${project?.id}`;
        break;
    }
  };

  const handleExampleClick = (_e: React.MouseEvent<HTMLDivElement>, project: Project) => {
    if (selectedProjectId === project.id) {
      setSelectedProjectId(null);
      onProjectSelect?.(null);
    } else {
      setSelectedProjectId(project.id);
      onProjectSelect?.(project.id);
    }
  };

  const ProjectCard = ({ project, isExample = false }: { project: Project; isExample?: boolean }) => {
    const [showMenu, setShowMenu] = useState(false);

    const handleMenuClick = (e: React.MouseEvent) => {
      e.stopPropagation();

      if (isExample) {
        handleContextMenu(e, project, 'exampleProject');
        return;
      }

      setShowMenu(!showMenu);
    };

    return (
      <div
        className="group cursor-pointer relative"
        onClick={(e) => {
          if (isExample) {
            handleExampleClick(e, project);
          } else {
            handleProjectClick(e, project);
          }
        }}
        onDoubleClick={() => {
          if (isExample) {
            onTryExample?.(project.id);
          }
        }}
        onContextMenu={(e) => handleContextMenu(e, project, isExample ? 'exampleProject' : 'project')}
        style={{ width: getCardSize().width }}
      >
        {selectedProjectId === project.id && (
          <div
            className="absolute top-2 right-2 w-5 h-5 border border-green01 rounded flex items-center justify-center z-10"
            style={{ backgroundColor: 'transparent' }}
          >
            <div className="w-3.5 h-3.5 bg-green01 rounded-sm" />
          </div>
        )}

        {/* Three dots menu button - visible on small screens */}
        <button
          onClick={handleMenuClick}
          className="absolute top-2 left-2 w-8 h-8 bg-black/70 rounded-full flex items-center justify-center md:opacity-0 md:group-hover:opacity-100 opacity-100 transition-opacity duration-200 z-20"
        >
          <MoreVertical className="w-5 h-5 text-white" />
        </button>

        {!isExample && showMenu && (
          <>
            <div
              className="fixed inset-0 z-30"
              onClick={(e) => {
                e.stopPropagation();
                setShowMenu(false);
              }}
            />
            <div className="absolute top-12 left-2 z-40 bg-greyP5 rounded-lg shadow-xl py-2 min-w-[120px]">
              <button
                className="w-full px-4 py-2 text-left text-sm text-white hover:bg-greyP4"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowMenu(false);
                  window.location.href = `/workspace?projectId=${project.id}`;
                }}
              >
                {t('common.open')}
              </button>
              <button
                className="w-full px-4 py-2 text-left text-sm text-white hover:bg-greyP4"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowMenu(false);
                  onOpenRenameModal?.(project);
                }}
              >
                {t('common.rename')}
              </button>
              <button
                className="w-full px-4 py-2 text-left text-sm text-white hover:bg-greyP4"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowMenu(false);
                  onOpenMoveModal?.(project);
                }}
              >
                {t('common.move')}
              </button>
              {isAdmin && (
                <button
                  className="w-full px-4 py-2 text-left text-sm text-white hover:bg-greyP4"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowMenu(false);
                    onSetProjectTemplate?.(project.id);
                  }}
                >
                  {t('projects.setAsTemplate')}
                </button>
              )}
              <button
                className="w-full px-4 py-2 text-left text-sm text-white hover:bg-greyP4"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowMenu(false);
                  onOpenDeleteModal?.(project);
                }}
              >
                {t('common.delete')}
              </button>
            </div>
          </>
        )}

        <div
          className={`bg-black rounded-lg overflow-hidden mb-1.5 flex items-center justify-center border-2 transition-colors duration-200 ${
            selectedProjectId === project.id ? 'border-green01' : 'border-greyP5 hover:border-greyP6'
          }`}
          style={getCardSize()}
        >
          <img src={project.previewImageUrl} alt={project.name} className="w-full h-full object-cover" />
        </div>

        {/* Project Info */}
        <div className="space-y-1">
          <h3
            className={`text-white font-medium truncate block ${
              gridSize === 'small' ? 'text-sm' : gridSize === 'large' ? 'text-xl' : 'text-lg'
            }`}
            title={project.name}
          >
            {project.name}
          </h3>
          {!isExample && (
            <div className={`flex items-center text-gray-400 ${gridSize === 'small' ? 'text-xs' : 'text-sm'}`}>
              <span>{formatUpdatedDate(project.updatedAt)}</span>
            </div>
          )}
        </div>
      </div>
    );
  };

  const ProjectListItem = ({ project, isExample = false }: { project: Project; isExample?: boolean }) => (
    <div
      className={`cursor-pointer border-b transition-colors duration-200 ${
        selectedProjectId === project.id ? 'bg-blackP2 border-green01' : 'bg-black border-greyP4 hover:bg-greyP5'
      }`}
      onClick={(e) => handleProjectClick(e, project)}
      onContextMenu={(e) => handleContextMenu(e, project, isExample ? 'exampleProject' : 'project')}
    >
      <div className="grid grid-cols-5 gap-4 px-6 py-4 items-center">
        {/* Project Name */}
        <div className="flex items-center space-x-3">
          <span
            className={`text-sm font-medium truncate ${
              selectedProjectId === project.id ? 'text-green01' : 'text-white'
            }`}
            title={project.name}
          >
            {project.name}
          </span>
        </div>

        {/* Last Edit */}
        <div className="text-gray-400 text-sm">{isExample ? '' : formatUpdatedDate(project.updatedAt)}</div>

        {/* Members */}
        <div></div>
        {/* Status */}
        <div className={`text-sm font-medium `}></div>

        {/* Optimization */}
        <div className="flex items-center text-white text-sm"></div>
      </div>
    </div>
  );

  const FolderListItem = ({ folder }: { folder: Folder }) => (
    <div
      className={`cursor-pointer border-b transition-colors duration-200 bg-black border-greyP4 hover:bg-greyP5`}
      onClick={() => onFolderSelect(folder.id)}
      onContextMenu={(e) => handleContextMenu(e, folder, 'folder')}
    >
      <div className="grid grid-cols-5 gap-4 px-6 py-4 items-center">
        {/* Project Name */}
        <div className="flex flex-row items-center space-x-3">
          <FolderIconList className="w-5 h-5 text-white" />
          <span className={`text-sm font-medium text-white`}>{folder.name}</span>
        </div>

        <div className="text-gray-400 text-sm"></div>

        {/* Members */}
        <div></div>

        {/* Status */}
        <div className={`text-sm font-medium`}></div>

        {/* Optimization */}
        <div className="flex items-center text-white text-sm"></div>
      </div>
    </div>
  );

  const renderFolderItem = (folder: Folder) => {
    if (!folder.id || !folder.name) return null;

    return (
      <div
        key={`folder-${folder.id}`}
        className="group cursor-pointer relative"
        onClick={() => onFolderSelect(folder.id)}
        onContextMenu={(e) => handleContextMenu(e, folder, 'folder')}
      >
        <div
          className="bg-black rounded-lg overflow-hidden mb-1.5 flex items-center justify-center border-2 transition-colors duration-200 border-greyP5 hover:border-greyP6"
          style={getCardSize()}
        >
          <FolderIconNew
            className={`${windowWidth < 768 ? 'w-10 h-10' : gridSize === 'small' ? 'w-10 h-10' : gridSize === 'large' ? 'w-13 h-13' : 'w-16 h-16'} text-white`}
          />
        </div>

        <div className="space-y-1">
          <h3 className="text-white font-medium text-lg">{folder.name}</h3>
        </div>
      </div>
    );
  };

  const backButtonItemId = breadcrumbs.length > 2 ? breadcrumbs[breadcrumbs.length - 2].id : breadcrumbs[0].id;

  /** "Your Projects" (at the top level) with the search next to it; inside a folder just the search. */
  const titleRow = (titleClass: string) =>
    (breadcrumbs.length == 1 || onSearchChange) && (
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        {breadcrumbs.length == 1 ? <h2 className={titleClass}>{t('projects.sidebar.yourProjects')}</h2> : <span />}
        {onSearchChange && (
          <DashboardSearch value={searchValue} onChange={onSearchChange} placeholder={t('navbar.search_project')} />
        )}
      </div>
    );

  if (viewMode === 'list') {
    return (
      <div ref={scrollRootRef} className="bg-black flex-1 overflow-auto" style={{ height: 'calc( 100vh - 224px)' }}>
        <div className="p-4 space-y-2">
          {breadcrumbs.length > 1 && (
            <div key={`breadcrumbs`} className="flex items-center justify-start gap-7">
              {breadcrumbs.map((item, index) => (
                <React.Fragment key={`breadcrumb-wrapper-${item.id ?? index}`}>
                  {index === 0 && (
                    <button
                      key="root"
                      onClick={() => handleBreadcrumbClick(backButtonItemId)}
                      className={`flex items-center justify-between px-6 py-3 rounded-full font-medium transition-colors duration-200 text-xs border border-gray-600 text-white hover:bg-greyP5 hover:border-gray-500`}
                      style={{
                        height: '36px',
                        paddingTop: '10px',
                        paddingBottom: '10px',
                        paddingLeft: '18px',
                        paddingRight: '18px',
                        gap: '8px',
                      }}
                    >
                      <ChevronLeft size={16} color="white" />
                      <span>{t('common.back')}</span>
                    </button>
                  )}

                  {item.id !== undefined && (
                    <div
                      key={`breadcrumb-${item.id}`}
                      className={`flex text-greyP2 justify-center cursor-pointer items-center gap-3  ${
                        index === breadcrumbs.length - 1 ? `text-[${green01}]` : 'text-white'
                      } ${index === breadcrumbs.length - 1 ? `hover:text-[${green01}]` : 'text-white/80'}`}
                      onClick={() => handleBreadcrumbClick(item.id)}
                    >
                      <FolderIconNew className="w-5 h-5 text-gray-400" />
                      <span>{item.name}</span>
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
          )}

          {/* Examples Section */}
          {exampleProjects.length > 0 && (
            <div>
              <h2
                className={`text-greyP2 font-medium mb-4 ${
                  gridSize === 'small' ? 'text-lg' : gridSize === 'large' ? 'text-2xl' : 'text-xl'
                }`}
              >
                {t('projects.sidebar.examples')}
              </h2>
              {isLoading ? (
                <div className="flex items-center justify-center py-6">
                  <LoadingSpinner size={24} color="#B3EC49" />
                </div>
              ) : (
                <div className="space-y-0 mb-4">
                  {exampleProjects.map((project) => (
                    <ProjectListItem key={`ex-${project.id}`} project={project} isExample={true} />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Your Projects Section */}
          {titleRow(
            `text-greyP2 font-medium ${gridSize === 'small' ? 'text-lg' : gridSize === 'large' ? 'text-2xl' : 'text-xl'}`,
          )}

          {/* Table Header */}
          <div className="grid grid-cols-5 gap-4 px-6 py-3 border-b border-greyP4 mb-4">
            <div className="text-gray-400 text-sm font-medium">Name</div>
            <div className="text-gray-400 text-sm font-medium">Last edit</div>
            <div className="text-gray-400 text-sm font-medium">Members</div>
            <div className="text-gray-400 text-sm font-medium">Status</div>
            <div className="text-gray-400 text-sm font-medium">Optimization</div>
          </div>

          {isLoading ? (
            <div className="flex flex-col items-center justify-center h-24 text-xl text-greyP2">
              Loading projects...
            </div>
          ) : (
            <>
              {hasProjectsOrFolderContent() ? (
                <>
                  <div className="space-y-0">
                    {folders.map((folder) => {
                      return folder.children.map((child) => <FolderListItem key={child.id} folder={child} />);
                    })}

                    {projects.map((project) => (
                      <ProjectListItem key={project.id} project={project} />
                    ))}
                  </div>
                  {/* Infinite scroll sentinel and spinner for list view */}
                  <div ref={sentinelRef} className="w-full h-4" />
                  {isLoadingMore && (
                    <div className="flex items-center justify-center py-4">
                      <LoadingSpinner size={24} color="#B3EC49" />
                    </div>
                  )}
                </>
              ) : (
                <>
                  {!folder?.id && (
                    <div className="flex flex-col items-center p-5 justify-center gap-4 text-3xl text-greyP2">
                      <div className="flex flex-row items-center gap-5">
                        <EmptyProjects className="w-24 h-24" />
                        <p>No projects yet</p>
                      </div>

                      <button
                        onClick={handleCreateNewProject}
                        className={`flex items-center px-6 py-3 rounded-full font-medium transition-colors duration-200 text-xs bg-white text-black hover:bg-gray-100`}
                        style={{
                          height: '36px',
                          paddingTop: '10px',
                          paddingBottom: '10px',
                          paddingLeft: '18px',
                          paddingRight: '18px',
                          gap: '8px',
                        }}
                      >
                        <Plus className="w-5 h-5" />
                        New Project
                      </button>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div ref={scrollRootRef} className="bg-black flex-1 overflow-auto p-4" style={{ height: 'calc(100vh - 224px)' }}>
      <div className="space-y-2">
        {/* Examples Section */}
        {exampleProjects.length > 0 && (
          <div>
            <h2
              className={`text-greyP2 font-medium mb-4 ${
                gridSize === 'small' ? 'text-lg' : gridSize === 'large' ? 'text-2xl' : 'text-xl'
              }`}
            >
              {t('projects.sidebar.examples')}
            </h2>
            {isLoading ? (
              <div className="flex items-center justify-center py-6">
                <LoadingSpinner size={24} color="#B3EC49" />
              </div>
            ) : (
              <div className="grid grid-cols-auto-fit gap-4 md:gap-8" style={{ gridTemplateColumns: getGridColumns() }}>
                {exampleProjects.map((project) => (
                  <ProjectCard key={`ex-${project.id}`} project={project} isExample={true} />
                ))}
              </div>
            )}
          </div>
        )}

        {breadcrumbs.length > 1 && (
          <div key={`grid-breadcrumbs`} className="flex items-center justify-start gap-7">
            {breadcrumbs.map((item, index) => (
              <>
                {index === 0 && (
                  <button
                    key="root"
                    onClick={() => handleBreadcrumbClick(backButtonItemId)}
                    className={`flex items-center justify-between px-6 py-3 rounded-full font-medium transition-colors duration-200 text-xs border border-gray-600 text-white hover:bg-greyP5 hover:border-gray-500`}
                    style={{
                      height: '36px',
                      paddingTop: '10px',
                      paddingBottom: '10px',
                      paddingLeft: '18px',
                      paddingRight: '18px',
                      gap: '8px',
                    }}
                  >
                    <ChevronLeft size={16} color="white" />
                    <span>Back</span>
                  </button>
                )}

                {item.id !== undefined && (
                  <div
                    key={`breadcrumb-${item.id}`}
                    className={`flex text-greyP2 justify-center items-center cursor-pointer gap-3 ${
                      index === breadcrumbs.length - 1 ? `text-[${green01}]` : 'text-white'
                    } ${index === breadcrumbs.length - 1 ? `hover:text-[${green01}]` : 'text-white/80'}`}
                    onClick={() => handleBreadcrumbClick(item.id)}
                  >
                    <FolderIconNew className="w-5 h-5 text-gray-400" />
                    <span>{item.name}</span>
                  </div>
                )}
              </>
            ))}
          </div>
        )}

        {/* Your Projects Section */}
        <div>
          {titleRow(
            `text-greyP2 font-medium ${gridSize === 'small' ? 'text-lg' : gridSize === 'large' ? 'text-2xl' : 'text-xl'}`,
          )}

          {isLoading ? (
            <div className="flex flex-col items-center justify-center h-24 text-xl text-greyP2">
              {t('projects.loadingProjects')}
            </div>
          ) : (
            <>
              {hasProjectsOrFolderContent() ? (
                <>
                  <div
                    className="grid grid-cols-auto-fit gap-4 md:gap-8"
                    style={{ gridTemplateColumns: getGridColumns() }}
                  >
                    {folders.map((folder) => {
                      return folder.children.map((child) => renderFolderItem(child));
                    })}

                    {projects.map((project) => (
                      <ProjectCard key={project.id} project={project} />
                    ))}
                  </div>
                  {/* Infinite scroll sentinel and spinner for grid view */}
                  <div ref={sentinelRef} className="w-full h-4" />
                  {isLoadingMore && (
                    <div className="flex items-center justify-center py-4">
                      <LoadingSpinner size={24} color="#B3EC49" />
                    </div>
                  )}
                </>
              ) : (
                <div className="flex flex-col items-center justify-center gap-4 text-3xl text-greyP2">
                  <EmptyProjects />
                  <p>{t('projects.noProjectsYet')}</p>
                  <button
                    onClick={handleCreateNewProject}
                    className={`flex items-center px-6 py-3 rounded-full font-medium transition-colors duration-200 text-xs bg-white text-black hover:bg-gray-100`}
                    style={{
                      height: '36px',
                      paddingTop: '10px',
                      paddingBottom: '10px',
                      paddingLeft: '18px',
                      paddingRight: '18px',
                      gap: '8px',
                    }}
                  >
                    <Plus className="w-5 h-5" />
                    {t('projects.sidebar.newProject')}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProjectGrid;
